/*
 * aura-grid.js: "load more with memory" for the catalog (spec 06).
 * Batches load when the shopper is 1.5 screens from the end, a set number of times, then a
 * "Show more" button takes over (from the start on save-data or 2G). Each batch comes from the
 * Section Rendering API and updates ?page=n with replaceState. Before leaving for a product the
 * grid stores the page range, the card and its offset; Back rebuilds earlier pages and scrolls
 * the same card to the same place. Without JavaScript the Next link is a real ?page=n link.
 * The Film / Color / Product type filters (aura-filters.js) call loadAll() and then restore() themselves.
 *
 * For you: collections have no sort menu (sections/catalog.liquid). For a shopper with something to
 * go on (aura-taste.js) a collection comes in their order, unless a link or Ask Aura (aura.sort) gave
 * it a ?sort_by; with nothing to go on it keeps its own order (Shift and Nebula colors last, below). Every card arrives at once from sections/catalog-pool.liquid (250 a request,
 * with the same filters) and aura.taste.rank() orders them; the grid then shows them a batch at a
 * time, the same way, from memory, and leaves ?page= alone. The cards wait out of sight until then
 * (section-catalog.css) and keep the collection's order if that takes longer than WAIT. The order is
 * kept for the session per URL, so Back finds the same grid even though the product page changed the taste.
 *
 * Shift and Nebula colors last: with nothing to go on, a collection keeps its own order but its Shift and
 * Nebula colors come after the rest (aura.taste.sink). The server already puts the first batch that way
 * (sections/catalog.liquid), so it shows at once; the pool for the rest comes once the page has loaded and
 * the browser is idle (start()), so the next batch shows from memory.
 *
 * The pool goes through window.auraPool (theme.liquid): one fetch per URL, which theme.liquid starts while
 * the page is still loading for a shopper with something to go on, kept a few minutes for Back and a
 * second visit.
 */
(() => {
  const aura = window.aura;
  const track = (n, p) => aura && aura.track(n, p);
  const POOL = 250;
  const WAIT = 2300; /* under the CSS's 2.5 s, which shows the cards whatever happens */
  /* true while customElements.define() upgrades the grid that came with the page */
  let upgrading = false;
  /* After the page's load, when the browser has a moment */
  const whenIdle = (fn) => {
    const go = () => ('requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 3000 }) : setTimeout(fn, 300));
    if (document.readyState === 'complete') go();
    else addEventListener('load', go, { once: true });
  };
  const aborted = (signal) => new Promise((_, reject) => signal.addEventListener('abort', () => reject(new DOMException('Pool aborted', 'AbortError')), { once: true }));

  class AuraGrid extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-grid-items]');
      this.more = this.querySelector('[data-load-more]');
      this.prev = this.querySelector('[data-load-prev]');
      this.status = this.querySelector('[data-grid-status]');
      this.page = Number(this.dataset.page) || 1;
      this.firstPage = this.page;
      this.pages = Number(this.dataset.pages) || 1;
      this.total = Number(this.dataset.total) || 0;
      this.autoLeft = Number(this.dataset.auto || 0);
      this.size = this.list.children.length;
      const conn = navigator.connection;
      if (conn && (conn.saveData || /2g/.test(conn.effectiveType || ''))) this.autoLeft = 0;

      /* For you. The URL is read a beat later: a sort or filter swaps this grid in before aura-filters.js
         pushes the new URL. Without one, theme.liquid's mark comes off now; the server has put the first
         batch in order with the Shift and Nebula colors last, and the pool for the rest comes when idle */
      const taste = aura && aura.taste;
      const handle = aura && aura.config.handle;
      const first = taste && aura.config.page_type === 'collection' && this.page === 1;
      this.born = upgrading ? 0 : performance.now();
      if (first && taste.ready(handle)) {
        this.dataset.forYou = 'pending';
        this.ready = Promise.resolve().then(() => this.personalize(true));
      } else {
        delete document.documentElement.dataset.forYou;
        this.lazy = !!(first && taste.sinks(handle));
        this.ready = Promise.resolve();
        if (this.lazy) whenIdle(() => this.start());
      }

      this.more?.addEventListener('click', (e) => { e.preventDefault(); this.loadNext('button'); });
      this.prev?.addEventListener('click', (e) => { e.preventDefault(); this.loadPrev(); });
      this.list.addEventListener('click', (e) => {
        const a = e.target.closest('a[href]');
        if (!a) return;
        this.remember(a);
        const li = a.closest('li');
        track('aura_card_clicked', { handle: li?.dataset.handle, position: [...this.list.children].indexOf(li) + 1, image: 'render', for_you: this.dataset.forYou === 'on' });
      });

      const sentinel = this.querySelector('[data-grid-sentinel]');
      if (sentinel && 'IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting && this.autoLeft > 0 && !this.loading && this.page < this.pages) {
            this.autoLeft -= 1;
            this.loadNext('auto');
          }
        }, { rootMargin: '0px 0px 150% 0px' });
        this.io.observe(sentinel);
      }
      const kinds = (this.closest('aura-filters')?.dataset.group || '').split(' ').filter(Boolean);
      const params = new URLSearchParams(location.search);
      if (!kinds.some((k) => params.has(k))) this.ready.then(() => this.restore());
    }

    /* forYou: the shopper's order (aura.taste.rank), else the collection's own with the Shift and Nebula
       colors last (aura.taste.sink). since: when the cards started waiting */
    async personalize(forYou, since = this.born) {
      const html = document.documentElement;
      const done = (on) => {
        delete html.dataset.forYou;
        this.dataset.forYou = on && forYou ? 'on' : 'off';
      };
      const params = new URLSearchParams(location.search);
      if (params.has('sort_by') || Number(params.get('page')) > 1) return done(false);
      /* the page's cards have waited since the page started, a swapped-in grid's since it came in */
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), Math.max(0, WAIT - (performance.now() - since)));
      const orderKey = `aura.for_you:${this.key().slice('aura.grid:'.length)}`;
      let saved = null;
      if (this.born === 0 && performance.getEntriesByType('navigation')[0]?.type === 'back_forward') {
        try { saved = JSON.parse(sessionStorage.getItem(orderKey)); } catch (e) { /* private mode */ }
      }
      try {
        const [table, pages] = await Promise.all([
          Array.isArray(saved) || !forYou ? null : aura.taste.table(),
          Promise.all(Array.from({ length: Math.max(1, Math.ceil(this.total / POOL)) }, (_, i) => this.fetchPool(i + 1, ctrl.signal)))
        ]);
        if (ctrl.signal.aborted) return done(false);
        const items = pages.flat().map((li, i) => ({
          li, i, handle: li.dataset.handle, collections: (li.dataset.taste || '').split(' ').filter(Boolean), available: li.dataset.available === 'true'
        }));
        if (!items.length) return done(false);
        let order;
        if (Array.isArray(saved)) {
          const at = new Map(saved.map((h, i) => [h, i]));
          order = items.slice().sort((a, b) => (at.get(a.handle) ?? Infinity) - (at.get(b.handle) ?? Infinity) || a.i - b.i);
        } else if (table) order = aura.taste.rank(items, table, aura.config.handle);
        else order = aura.taste.sink(items);
        /* saved once the page is shown: a prerendered page's sessionStorage is replaced when it's opened */
        const saveOrder = () => { try { sessionStorage.setItem(orderKey, JSON.stringify(order.map((it) => it.handle))); } catch (e) { /* private mode */ } };
        (window.auraShown || Promise.resolve()).then(saveOrder);
        /* the cards already here keep their images */
        const here = new Map([...this.list.children].map((li) => [li.dataset.handle, li]));
        this.queue = order.map((it) => here.get(it.handle) || it.li);
        this.list.replaceChildren(...this.queue.splice(0, this.size));
        this.pages = this.page + Math.ceil(this.queue.length / this.size);
        if (!this.queue.length) this.finish();
        return done(true);
      } catch (e) {
        return done(false);
      } finally {
        clearTimeout(timer);
      }
    }

    /* The Shift and Nebula colors last after a first batch the server put in order: the pool comes when idle,
       or sooner if more is needed */
    start() {
      if (this.lazy) {
        this.lazy = false;
        this.ready = this.personalize(false, performance.now());
      }
      return this.ready;
    }

    async fetchPool(n, signal) {
      const u = new URL(location.href);
      u.searchParams.delete('page');
      if (n > 1) u.searchParams.set('page', n);
      u.searchParams.set('section_id', 'catalog-pool');
      const html = await (window.auraPool
        ? Promise.race([window.auraPool(u.href), aborted(signal)])
        : fetch(u, { signal }).then((res) => (res.ok ? res.text() : Promise.reject(new Error(`pool ${res.status}`)))));
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return [...doc.querySelectorAll('[data-pool-items] > li')];
    }

    /* Cards not in the grid yet: the server's first batch can hold colors from later pages */
    unseen(items) {
      const have = new Set([...this.list.children].map((li) => li.dataset.handle));
      return items.filter((li) => !have.has(li.dataset.handle));
    }

    /* For you: the next batch from memory, or every card left */
    showQueued(count) {
      const items = this.queue.splice(0, count);
      this.list.append(...items);
      this.page = Math.min(this.pages, this.page + Math.ceil(items.length / this.size));
      if (!this.queue.length) this.finish();
      return items;
    }

    finish() {
      this.page = this.pages;
      this.more?.remove();
      this.io?.disconnect();
    }

    key() {
      const u = new URL(location.href);
      u.searchParams.delete('page');
      return `aura.grid:${u.pathname}${u.search}`;
    }

    pageUrl(n, forFetch) {
      const u = new URL(location.href);
      u.searchParams.set('page', n);
      if (forFetch) u.searchParams.set('section_id', this.dataset.sectionId);
      return u;
    }

    async fetchPage(n) {
      const res = await fetch(this.pageUrl(n, true));
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      return [...doc.querySelectorAll('[data-grid-items] > li')];
    }

    async loadNext(mode) {
      if (this.loading || this.page >= this.pages) return;
      this.loading = true;
      await this.start();
      if (this.queue) {
        if (this.queue.length) {
          this.announce(this.showQueued(this.size).length);
          track('aura_batch_loaded', { batch: this.page, mode, ms: 0 });
        }
        this.loading = false;
        return;
      }
      this.more?.setAttribute('aria-busy', 'true');
      const t0 = performance.now();
      try {
        const items = this.unseen(await this.fetchPage(this.page + 1));
        if (this.all) { this.loading = false; return; } /* loadAll() fetches this batch too */
        this.list.append(...items);
        this.page += 1;
        history.replaceState(history.state, '', this.pageUrl(this.page));
        this.announce(items.length);
        track('aura_batch_loaded', { batch: this.page, mode, ms: Math.round(performance.now() - t0) });
      } catch (e) {
        /* the Next link still works */
      }
      this.loading = false;
      this.more?.removeAttribute('aria-busy');
      if (this.page >= this.pages) {
        this.more?.remove();
        this.io?.disconnect();
      } else if (this.more) {
        const next = this.pageUrl(this.page + 1);
        this.more.href = next.pathname + next.search;
      }
    }

    /* Every batch, in order, once. Without the pool's order (a ?sort_by or a later ?page) every card comes
       from the pool anyway, 250 a request in the same order, not a batch a request (/collections/all is 19);
       the cards already here keep their images */
    loadAll() {
      this.all ??= (async () => {
        await this.start();
        if (this.queue) {
          this.showQueued(this.queue.length);
          return;
        }
        const signal = new AbortController().signal;
        const pages = await Promise.all(Array.from({ length: Math.max(1, Math.ceil(this.total / POOL)) }, (_, i) => this.fetchPool(i + 1, signal)));
        const here = new Map([...this.list.children].map((li) => [li.dataset.handle, li]));
        this.list.replaceChildren(...pages.flat().map((li) => here.get(li.dataset.handle) || li));
        this.firstPage = 1;
        this.page = this.pages;
        this.prev?.closest('.catalog__prev')?.remove();
        this.more?.remove();
        this.io?.disconnect();
      })().catch((e) => { this.all = null; throw e; });
      return this.all;
    }

    async loadPrev() {
      if (this.loading || this.firstPage <= 1) return;
      this.loading = true;
      const anchor = this.list.firstElementChild;
      const before = anchor ? anchor.getBoundingClientRect().top : 0;
      try {
        const items = await this.fetchPage(this.firstPage - 1);
        this.list.prepend(...items);
        this.firstPage -= 1;
        if (anchor) window.scrollBy(0, anchor.getBoundingClientRect().top - before);
        this.announce(items.length);
      } catch (e) { /* link still works */ }
      this.loading = false;
      if (this.firstPage <= 1) this.prev?.closest('.catalog__prev')?.remove();
    }

    announce(added) {
      if (!this.status) return;
      const shown = this.list.children.length;
      const tpl = (aura && aura.config.strings.loaded_more) || '';
      this.status.textContent = tpl.replace('[count]', added).replace('[shown]', shown).replace('[total]', this.total);
    }

    remember(a) {
      const li = a.closest('li');
      if (!li) return;
      try {
        sessionStorage.setItem(this.key(), JSON.stringify({ first: this.firstPage, handle: li.dataset.handle, top: li.getBoundingClientRect().top }));
      } catch (e) { /* private mode */ }
    }

    async restore() {
      let saved = null;
      try { saved = JSON.parse(sessionStorage.getItem(this.key())); } catch (e) { /* ignore */ }
      if (!saved) return;
      try { sessionStorage.removeItem(this.key()); } catch (e) { /* ignore */ }
      const nav = performance.getEntriesByType('navigation')[0];
      if (!nav || nav.type !== 'back_forward') return;
      const from = Number(saved.first) || 1;
      if (from < this.firstPage) {
        const pages = [];
        for (let n = from; n < this.firstPage; n += 1) pages.push(n);
        const batches = await Promise.all(pages.map((n) => this.fetchPage(n)));
        this.list.prepend(...batches.flat());
        this.firstPage = from;
        if (this.firstPage <= 1) this.prev?.closest('.catalog__prev')?.remove();
      }
      /* For you or Shift and Nebula last: the batches up to the card come back from memory */
      if (this.lazy && !this.list.querySelector(`li[data-handle="${CSS.escape(saved.handle)}"]`)) await this.start();
      const queued = this.queue ? this.queue.findIndex((li) => li.dataset.handle === saved.handle) : -1;
      if (queued >= 0) this.showQueued(Math.ceil((queued + 1) / this.size) * this.size);
      const card = this.list.querySelector(`li[data-handle="${CSS.escape(saved.handle)}"]`);
      if (card) {
        window.scrollTo({ top: card.getBoundingClientRect().top + window.scrollY - saved.top, behavior: 'instant' });
        card.querySelector('a')?.focus({ preventScroll: true });
      }
      track('aura_scroll_restored', { ok: !!card, method: 'rebuilt' });
    }
  }

  window.addEventListener('pageshow', (e) => { if (e.persisted) track('aura_scroll_restored', { ok: true, method: 'bfcache' }); });
  if (!customElements.get('aura-grid')) {
    upgrading = true;
    customElements.define('aura-grid', AuraGrid);
    upgrading = false;
  }
})();
